import React from 'react';
import { Button } from 'antd';
import { css } from '@emotion/react';
import Tooltip from 'components/common/tooltip';
import styled from 'types/theme.types';
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';

/** Figma's Icon button types (Design system 114:2430). */
export type IconButtonType = 'primary' | 'secondary';

type IconButtonSize = 'small' | 'middle' | 'large';

export const IconButton: React.FunctionComponent<{
  /** Omit where Figma specs no tooltip. */
  tooltipTitleId?: string;
  icon: React.FC<Omit<AntdIconProps, 'ref'> & React.RefAttributes<HTMLSpanElement>>;
  dataE2e: string;
  /** Figma's Type. Omitted, the button keeps the look it had before the types existed. */
  type?: IconButtonType;
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  size?: IconButtonSize;
  /** A button wrapping the icon needs this or it is unnamed. */
  ariaLabel?: string;
  /** For a button that discloses content (the tree chevron): the aria-expanded state. */
  ariaExpanded?: boolean;
  /** -1 for a button inside a link, so the anchor is the action's only tab stop. */
  tabIndex?: number;
  /** Forwarded so a call site can extend this with `styled(IconButton)` instead of a wrapper div. */
  className?: string;
}> = ({
  tooltipTitleId,
  icon: Icon,
  dataE2e,
  type,
  onClick,
  disabled,
  size = 'middle',
  ariaLabel,
  ariaExpanded,
  tabIndex,
  className
}) => {
  const StyledButton = type ? TYPED_ICON_BUTTONS[type] : StyledIconButton;

  const button = (
    <StyledButton
      type="text"
      size={size}
      icon={<Icon />}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      tabIndex={tabIndex}
      data-e2e={dataE2e}
      onClick={onClick}
      disabled={disabled}
      className={className}
    />
  );

  if (!tooltipTitleId) return button;

  return (
    <Tooltip titleId={tooltipTitleId} placement="top">
      {button}
    </Tooltip>
  );
};

const FIGMA_PADDING: Record<IconButtonSize, string> = {
  small: 'var(--padding-xxs)',
  middle: 'var(--padding-xs)',
  large: 'var(--padding-md)'
};

/**
 * Figma's Icon button at one size: the 16px icon inside an even padding, which makes 28, 32 and 40. antd
 * pads an icon-only button with 0 and sizes its icon from the label, so every value is stated, at three
 * classes to outrank antd's icon-only rules (two classes for the box, three for the icon).
 */
const figmaGeometry = (size: IconButtonSize) => {
  const padding = FIGMA_PADDING[size];
  const edge = `calc(var(--scale-500) + 2 * ${padding})`;

  return css`
    &&& {
      width: ${edge};
      height: ${edge};
      min-width: ${edge};
      padding: ${padding};
      border: none;
      border-radius: var(--border-radius-100);

      .anticon {
        font-size: var(--scale-500);
      }
    }
  `;
};

/** No `type`: the look from before the types. Only small takes Figma's geometry; the other sizes keep antd's. */
const StyledIconButton = styled(Button)`
  color: var(--icon-primary-standalone);

  &:not(:disabled):hover {
    color: var(--icon-primary-hover);
  }

  /* The design's grey hover surface (Figma EZ Design system, node 114-2430). Doubled class because antd
     declares the hover background at two classes and its tag is injected later; up to antd 5.6 antd's own
     grey outranked this rule anyway, and 5.29 lets it through, so it has to be the design's colour. */
  &&:not(:disabled):hover {
    background-color: var(--surface-main-element-on-card);
  }

  &:disabled {
    color: var(--icon-disabled);
  }

  ${({ size }) => size === 'small' && figmaGeometry('small')}
`;

/**
 * Figma's Primary and Secondary differ only in the icon's colour. Focused is antd's ring, the one every
 * button and field shows (`focusRingTokens` in antd.constants), rather than Figma's thinner line at a
 * wider gap: the call the text Button made too.
 */
const typedIconButton = (iconColor: string) => styled(Button)`
  ${({ size }) => figmaGeometry(size ?? 'middle')}

  &&& {
    color: ${iconColor};
  }

  /* Pressed is not designed and matches Hovered (D1). Three classes and two pseudo-classes outrank antd's
     text-button hover and pressed (0,4,0) whichever order the style tags land in. */
  &&&:not(:disabled):hover,
  &&&:not(:disabled):active {
    background-color: var(--surface-secondary-action-hover);
    color: ${iconColor};
  }

  &&&:disabled {
    color: var(--icon-disabled);
  }
`;

const TYPED_ICON_BUTTONS: Record<IconButtonType, ReturnType<typeof typedIconButton>> = {
  primary: typedIconButton('var(--icon-primary-standalone)'),
  secondary: typedIconButton('var(--icon-secondary-standalone)')
};
